import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SwitchInput from '@/components/Inputs/Switch';
import Tooltip from '@/components/Tooltip';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';

function InfoTip({ label, colors, styles }) {
  return (
    <Tooltip label={label} side="top" align="start" showIsXs delayDuration={150}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        hitSlop={8}
        style={({ hovered }) => [
          styles.infoButton,
          hovered && styles.infoButtonHovered,
        ]}
      >
        <Icon name="circle-info" size={14} color={colors.text_color_secondary} />
      </Pressable>
    </Tooltip>
  );
}

function MemoryPreferencesCard({
  isMemoryEnabled,
  isSaving = false,
  onToggleMemoryEnabled,
  isLearningEnabled,
  onToggleLearning,
  isPlus,
  onPlusUpsell,
}) {
  const { colors, isDark } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);
  const styles = useMemo(
    () => createStyles(colors, isXS, isSM),
    [colors, isXS, isSM]
  );

  const useDesc = t('pages.memory.useInConversationsDesc');
  const learnDesc = isPlus
    ? t('pages.memory.learnFromConversationsDesc')
    : t('pages.memory.learnFromConversationsDescFree');

  return (
    <View style={styles.card}>
      <Text
        size="s"
        weight="semibold"
        color="secondary"
        accessibilityRole="header"
        style={styles.heading}
      >
        {t('pages.memory.preferencesTitle')}
      </Text>

      <View style={styles.togglesGrid}>
        <View style={styles.controlBox}>
          <View style={styles.controlInfo}>
            <View style={styles.controlTitleRow}>
              <Text size="n" weight="semibold" color="primary">
                {t('pages.memory.useInConversations')}
              </Text>
              <InfoTip label={useDesc} colors={colors} styles={styles} />
            </View>
            <Text size="xs" color="secondary" style={styles.controlDesc}>
              {useDesc}
            </Text>
          </View>
          <View style={styles.switchCol}>
            <SwitchInput
              disabled={isSaving}
              accessibilityLabel={t('pages.memory.useInConversations')}
              value={isMemoryEnabled}
              onValueChange={onToggleMemoryEnabled}
            />
          </View>
        </View>

        <View style={styles.controlBox}>
          <View style={styles.controlInfo}>
            <View style={styles.controlTitleRow}>
              <Text size="n" weight="semibold" color="primary">
                {t('pages.memory.learnFromConversations')}
              </Text>
              {isPlus ? (
                <View style={styles.plusBadge}>
                  <Text size="xxs" weight="bold" style={styles.plusBadgeText}>
                    PLUS
                  </Text>
                </View>
              ) : null}
              <InfoTip label={learnDesc} colors={colors} styles={styles} />
            </View>
            <Text size="xs" color="secondary" style={styles.controlDesc}>
              {learnDesc}
            </Text>
            {!isPlus && (
              <Pressable onPress={onPlusUpsell} style={styles.upsellPressable}>
                <Icon name="sparkles" size={12} color={memColors.accent} />
                <Text size="xs" weight="semibold" style={{ color: memColors.accent }}>
                  {t('pages.memory.learnPlusUpsell')}
                </Text>
              </Pressable>
            )}
          </View>
          <View style={styles.switchCol}>
            <SwitchInput
              disabled={isSaving}
              accessibilityLabel={t('pages.memory.learnFromConversations')}
              value={isLearningEnabled}
              onValueChange={onToggleLearning}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, isXS, isSM) =>
  StyleSheet.create({
    card: {
      marginBottom: 20,
      gap: 10,
    },
    heading: {
      paddingHorizontal: 2,
    },
    togglesGrid: {
      flexDirection: isSM || isXS ? 'column' : 'row',
      gap: isXS ? 16 : 28,
    },
    controlBox: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    controlInfo: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    controlTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    infoButton: {
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      ...(isWeb ? { cursor: 'help' } : {}),
    },
    infoButtonHovered: {
      backgroundColor: colors.solid_color_secondary,
    },
    controlDesc: {
      lineHeight: 18,
    },
    switchCol: {
      flexShrink: 0,
      paddingTop: 2,
    },
    plusBadge: {
      backgroundColor: colors.solid_color_tertiary,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 6,
    },
    plusBadgeText: {
      color: colors.text_color_secondary,
      fontSize: 10,
    },
    upsellPressable: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginTop: 4,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
  });

export default React.memo(MemoryPreferencesCard);
